import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Tab, Nav, Table, Button, Badge } from 'react-bootstrap';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import api from '../services/bookingApi';

const SalonBookingModule = () => {
  const [appointments, setAppointments] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (token) {
      loadClientData();
    }
  }, [token]);

  const loadClientData = async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Get client-specific appointments (automatically filtered on the backend)
      const appointmentsData = await api.getAppointments();
      setAppointments(Array.isArray(appointmentsData) ? appointmentsData : []);

      // 2. Get client favorites
      const favRes = await axios.get('/api/auth/favorites', {
        headers: { Authorization: token }
      });
      setFavorites(favRes.data || []);
    } catch (err) {
      console.error("Error loading client data:", err);
      setError("Impossible de charger vos réservations.");
      if (err.status === 401 || err.message?.includes('401') || err.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/Login';
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler ce rendez-vous ?")) {
      return;
    }
    setError('');
    setSuccess('');
    try {
      await api.cancelAppointment(id);
      setSuccess("Rendez-vous annulé avec succès.");
      loadClientData(); // reload
    } catch (err) {
      console.error("Error cancelling appointment:", err);
      setError("Erreur lors de l'annulation du rendez-vous.");
    }
  };

  const handleRemoveFavorite = async (providerId) => {
    try {
      await axios.post(`/api/auth/favorites/${providerId}`, {}, {
        headers: { Authorization: token }
      });
      setFavorites(prev => prev.filter(f => f._id !== providerId));
    } catch (err) {
      console.error("Error removing favorite:", err);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      'confirmed': { bg: 'success', text: 'Confirmé' },
      'pending': { bg: 'warning', text: 'En attente' },
      'cancelled': { bg: 'danger', text: 'Annulé' },
      'completed': { bg: 'info', text: 'Complété' }
    };
    const c = configs[status] || { bg: 'secondary', text: status };
    return <Badge bg={c.bg} className="px-3 py-2 text-uppercase">{c.text}</Badge>;
  };

  const renderStars = (rating = 5) => {
    return "★".repeat(Math.round(rating)) + "☆".repeat(5 - Math.round(rating));
  };

  return (
    <Container style={{ marginTop: '100px', marginBottom: '40px', fontFamily: "'Outfit', sans-serif" }}>
      <div className="p-4 mb-4 bg-white rounded-4 shadow-sm border-0 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 border-bottom">
        <div>
          <h2 className="text-primary font-weight-bold mb-1">📅 Mon Espace Réservations</h2>
          <p className="text-muted mb-0">Consultez vos rendez-vous programmés et gérez vos prestataires favoris</p>
        </div>
        <Button variant="outline-primary" onClick={loadClientData} disabled={loading}>
          {loading ? 'Chargement...' : 'Actualiser'}
        </Button>
      </div>

      {error && <div className="alert alert-danger mb-4">{error}</div>}
      {success && <div className="alert alert-success mb-4">{success}</div>}

      <Tab.Container id="client-space-tabs" defaultActiveKey="appointments">
        <Row className="mb-4">
          <Col>
            <Nav variant="pills" className="bg-white p-2 rounded-3 shadow-sm d-inline-flex gap-2">
              <Nav.Item>
                <Nav.Link eventKey="appointments" className="px-4 py-2 font-weight-bold">📋 Mes Rendez-vous</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="favorites" className="px-4 py-2 font-weight-bold">❤️ Mes Établissements Favoris</Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>
        </Row>

        <Tab.Content>
          {/* Tab 1: Appointments List */}
          <Tab.Pane eventKey="appointments">
            {loading ? (
              <div className="text-center py-5 text-muted">Chargement de vos rendez-vous...</div>
            ) : appointments.length === 0 ? (
              <div className="text-center py-5 my-3 bg-white shadow-sm rounded-4 text-muted border">
                <div className="fs-1 mb-3">📅</div>
                <p className="mb-3">Vous n'avez aucun rendez-vous programmé pour le moment.</p>
                <Link to="/">
                  <Button variant="primary" className="rounded-pill px-4">Réserver une activité</Button>
                </Link>
              </div>
            ) : (
              <Row className="g-4">
                {appointments.map(app => {
                  const isCancelled = app.status === 'cancelled';
                  const appDate = new Date(app.date);
                  const isUpcoming = appDate >= new Date() && !isCancelled;

                  return (
                    <Col key={app._id} xs={12} md={6}>
                      <Card className="border-0 shadow-sm rounded-4 overflow-hidden bg-white h-100">
                        <div style={{ height: '5px', background: isCancelled ? '#ef4444' : '#13223c' }}></div>
                        <Card.Body className="p-4 d-flex flex-column justify-content-between">
                          <div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <span className="small text-muted font-weight-bold">
                                📅 {format(parseISO(app.date), 'EEEE dd MMMM yyyy', { locale: fr })}
                              </span>
                              {getStatusBadge(app.status)}
                            </div>

                            <h4 className="font-weight-bold text-dark mb-1">
                              {app.services && app.services.length > 0 ? (
                                app.services.map(s => s.name).join(', ')
                              ) : (
                                app.service?.name || "Prestation standard"
                              )}
                            </h4>

                            <p className="text-primary font-weight-bold mb-3" style={{ fontSize: '15px' }}>
                              🏢 {app.provider?.businessName || "Bookly Services"}
                            </p>

                            <div className="d-flex gap-4 text-muted small mb-2">
                              <span>⏱️ Horaire : <strong>{app.startTime}</strong></span>
                              <span>💰 Total : <strong className="text-success">{app.totalPrice || app.service?.price || 0} DT</strong></span>
                            </div>
                            {(app.guestsCount > 1 || app.bookedResource) && (
                              <div className="d-flex gap-3 text-muted small mb-4">
                                {app.guestsCount > 1 && <span>👥 {app.guestsCount} pers.</span>}
                                {app.bookedResource && (
                                  <span>🪑 {typeof app.bookedResource === 'object' ? app.bookedResource.name : app.bookedResource}</span>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="d-flex gap-2 border-top pt-3">
                            <Button 
                              variant="primary" 
                              size="sm" 
                              className="rounded-pill px-4"
                              onClick={() => navigate('/QRCodeConfirmation.js', { state: { appointment: app } })}
                            >
                              Fiche QR Code 📱
                            </Button>
                            {isUpcoming && (
                              <Button 
                                variant="outline-danger" 
                                size="sm" 
                                className="rounded-pill px-4 ms-auto"
                                onClick={() => handleCancel(app._id)}
                              >
                                Annuler
                              </Button>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Tab.Pane>

          {/* Tab 2: Favorites List */}
          <Tab.Pane eventKey="favorites">
            {loading ? (
              <div className="text-center py-5 text-muted">Chargement de vos favoris...</div>
            ) : favorites.length === 0 ? (
              <div className="text-center py-5 my-3 bg-white shadow-sm rounded-4 text-muted border">
                <div className="fs-1 mb-3">❤️</div>
                <p className="mb-3">Vous n'avez pas encore d'établissements favoris.</p>
                <Link to="/">
                  <Button variant="primary" className="rounded-pill px-4">Découvrir des prestataires</Button>
                </Link>
              </div>
            ) : (
              <Row className="g-4">
                {favorites.map(fav => (
                  <Col key={fav._id} xs={12} md={6} lg={4}>
                    <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                      <div style={{ height: '4px', background: '#c69d4e' }}></div>
                      <Card.Body className="p-4 d-flex flex-column justify-content-between">
                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <span className="badge bg-secondary-subtle text-secondary px-3 py-1 text-uppercase small">
                              {fav.activity}
                            </span>
                            <button
                              onClick={() => handleRemoveFavorite(fav._id)}
                              style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer' }}
                            >
                              ❤️
                            </button>
                          </div>

                          <h5 className="font-weight-bold text-dark mb-2">{fav.businessName || fav.name}</h5>
                          <span className="text-warning small mb-3 d-block font-weight-bold">
                            {renderStars(fav.rating)} ({fav.rating || 5.0})
                          </span>
                          
                          <p className="text-muted small mb-4">
                            📍 {fav.adress || "Tunis, Tunisie"}
                          </p>
                        </div>

                        <Link to={`/Boutique/${fav._id}`}>
                          <Button variant="primary" className="w-100 rounded-pill py-2">
                            Visiter la boutique ➔
                          </Button>
                        </Link>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
};

export default SalonBookingModule;
